import { FC, useEffect, useState } from "react";
import { MultiplyIcon as CloseSvg } from "../../libs/corporate/icons/outlined/suggested/SymbolCollection";
import styles from "./alert.module.scss";
import classNames from "classnames";
import AlertIcon from "./alertIcon";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ApiErrorModalWindow } from "../modalWindows/ApiErrorModalWindow";
import { responseErrorType } from "../../@types/enumsGlobal";
import { ReactPortal } from "../modalWindows/ReactPortal";
import { DURATION_MSEC_ALERT, DURATION_MSEC_MODAL_ALERT } from "../../constants";
import { useSafeAlerts } from "./useSafeAlerts";
import { UISettingsManager } from "../../libs/ui-settings-manager/UISettingsManager";

interface IAlertProps {
    variant: AlertVariant,
    closeOnClick?: () => void,
    closable?: boolean,
    iconable?: boolean,
    global?: boolean
}

const Alert: FC<IAlertProps> = (props) => {

    function getAlertClasses() {
        switch (props.variant) {
            case "default": {
                return classNames(styles.alertInfo)
            }
            case "info": {
                return classNames(styles.alertInfo)
            }
            case "success": {
                return classNames(styles.alertSuccess)
            }
            case "danger": {
                return classNames(styles.alertDanger)
            }
            case "warning": {
                return classNames(styles.alertDanger)
            }
            case "error": {
                return classNames(styles.alertDanger)
            }
            case "exception": {
                return classNames(styles.alertDanger)
            }
        }
    }
    function getAlertIcon() {
        switch (props.variant) {
            case "default": {
                return (
                    <span>
                        <AlertIcon type="screamer" className={styles.alertIconInfo}></AlertIcon>
                    </span>
                )
            }
            case "info": {
                return (
                    <span>
                        <AlertIcon type="screamer" className={styles.alertIconInfo}></AlertIcon>
                    </span>
                )
            }
            case "success": {
                return (
                    <span>
                        <AlertIcon type="success" className={styles.alertIconSuccess}></AlertIcon>
                    </span>
                )
            }
            case "danger": {
                return (
                    <span>
                        <AlertIcon type="danger" className={styles.alertIconDanger}></AlertIcon>
                    </span>
                )
            }
            case "warning": {
                return (
                    <span>
                        <AlertIcon type="warning" className={styles.alertIconDanger}></AlertIcon>
                    </span>
                )
            }
            case "error": {
                return (
                    <span>
                        <AlertIcon type="warning" className={styles.alertIconDanger}></AlertIcon>
                    </span>
                )
            }
        }
    }
    function getCloseIcon() {
        switch (props.variant) {
            case "default": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconInfo}></CloseSvg>
                    </span>
                )
            }
            case "info": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconInfo}></CloseSvg>
                    </span>
                )
            }
            case "success": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconInfo}></CloseSvg>
                    </span>
                )
            }
            case "danger": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconDanger}></CloseSvg>
                    </span>
                )
            }
            case "warning": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconDanger}></CloseSvg>
                    </span>
                )
            }
            case "error": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconDanger}></CloseSvg>
                    </span>
                )
            }
            case "exception": {
                return (
                    <span>
                        <CloseSvg className={styles.closeIconDanger}></CloseSvg>
                    </span>
                )
            }
        }
    }
    return (
        <div className={classNames(getAlertClasses(), styles.messageWrapper)}>
            {props.iconable ? getAlertIcon() : null}
            <span className={styles.message}>{props.children}</span>
            {props.closable && (
                <span className={styles.closebtn} onClick={props.closeOnClick}>
                    {getCloseIcon()}
                </span>
            )}
        </div>
    )
}

interface IGlobalAlertProps {
    variant: AlertVariant,
    title: string
    instance?: string
    detail?: string
    statusCode?: number
    alertElementId: string,
    onClose: () => void,
    showDetails: boolean,
    durationMsec?: number,
    container?: Element
}
const ALERTS_EXCLUDE: { variant: AlertVariant; title: string }[] = [
    { variant: "success", title: "Сохранено " },
    // сюда можно добавить и другие сообщения
];

const GlobalAlert: FC<IGlobalAlertProps> = ({ onClose, durationMsec, container, ...props }) => {
    const alertsCtx = useSafeAlerts();
    const [settings, setSettings] = useState(() => UISettingsManager.getSettings().alerts);
    const [openMoreInfo, setOpenMoreInfo] = useState<boolean>(props.showDetails)
    const { t } = useTranslation();
    const baseT = (value: string) => t('errors.' + value)

    useEffect(() => {
        const handleSettingsChange = () => {
            const uiSettings = UISettingsManager.getSettings();
            setSettings(uiSettings.alerts);
        };

        window.addEventListener('uiSettingsChanged', handleSettingsChange);
        return () => window.removeEventListener('uiSettingsChanged', handleSettingsChange);
    }, []);

    const handleOpenDetails = () => {
        setOpenMoreInfo(true);
        if (props.alertElementId) {
            alertsCtx.markAsRead(props.alertElementId);
        }
    };
        
    const handleClose = () => {
        onClose?.();
    };

    const getDisplayDuration = () => {
        const shouldUseShortDuration = ALERTS_EXCLUDE.some(alert => 
            alert.variant === props.variant && 
            props.title?.includes(alert.title)
        );
        
        if (shouldUseShortDuration) {
            return 1000;
        }
        
        if (durationMsec !== undefined) {
            return durationMsec;
        }
        
        return settings.duration || DURATION_MSEC_ALERT;
    };

    const displayDuration = getDisplayDuration();

    useEffect(() => {
        if (openMoreInfo) {
            const tId = window.setTimeout(() => handleClose(), DURATION_MSEC_MODAL_ALERT);
            return () => clearTimeout(tId);
        } else {
            const tId = window.setTimeout(() => handleClose(), displayDuration);
            return () => clearTimeout(tId);
        }
    }, [openMoreInfo, displayDuration, container]);

    const returnMessage = () => {
        switch (props.variant) {
            case 'exception': {
                return <span onClick={() => handleOpenDetails()} className={styles.title}>
                    {props.title?.substring(0, 35) + '...'}
                </span>
            }
            case "error": {
                return <span onClick={() => handleOpenDetails()} className={styles.title}>{baseT('requestExecutionError')}</span>
            }
            case "warning": {
                return <span onClick={() => handleOpenDetails()} className={styles.title}>{baseT('warning')}</span>
            }
            default: {
                return props.title
            }
        }

    }
    return (
        <>
            <Alert variant={props.variant} global={true} closable={true} iconable={true} closeOnClick={handleClose}>
                {
                    returnMessage()
                }
            </Alert>
            {openMoreInfo &&
            <ReactPortal wrapperId="react-portal-modal-container-alert">
                <ApiErrorModalWindow
                    header={responseErrorType[props.variant]}
                    instance={props.instance ?? ''}
                    title={props.title}
                    detail={props.detail ?? ''}
                    status={props.statusCode}
                    ok={() => { setOpenMoreInfo(false) }}
                    cancel={() => { setOpenMoreInfo(false) }}
                />
            </ReactPortal>
            }
        </>
    )
}

export { GlobalAlert }
